/**
 * Gates the direct-to-dataLayer code paths that bypass GTM (customEvent,
 * userIdentify, page_view, and the CD-8600 signup consent-grant replay). The
 * GTM container script load itself sets no analytics cookies — downstream tags
 * (GA4, etc.) do, and those are gated by the Consent Mode v2 signals Civic
 * writes via dashboard-configured accept/revoke callbacks. So we deliberately
 * do not gate TagManager.initialize().
 *
 * Civic v9 persists the user's consent state as URL-encoded JSON in a
 * document cookie named "CookieControl". Reading the cookie directly avoids
 * Civic's JS API entirely (getCategoryConsent is index-based and fragile to
 * dashboard reordering; getCookie is a generic document-cookie reader that
 * requires a name argument, not a "get consent state" helper). This is
 * stable across Civic version bumps and works even before the Civic script
 * has finished loading, as long as the user has previously interacted with
 * the banner. Category names are case-sensitive and match the live Civic
 * dashboard config exactly.
 */

const CONSENT_COOKIE_NAME = 'CookieControl';
const ANALYTICS_CATEGORY = 'Analytics';

type CivicConsentCookie = {
  optionalCookies?: Record<string, 'accepted' | 'revoked'>;
};

const readConsentCookie = (): CivicConsentCookie | null => {
  if (typeof document === 'undefined') return null;
  const prefix = `${CONSENT_COOKIE_NAME}=`;
  const raw = document.cookie
    .split(';')
    .map((c) => c.trim())
    .find((c) => c.startsWith(prefix));
  if (!raw) return null;
  try {
    return JSON.parse(decodeURIComponent(raw.slice(prefix.length))) as CivicConsentCookie;
  } catch {
    return null;
  }
};

/**
 * Gates the direct-to-dataLayer analytics paths strictly on the `Analytics`
 * category. Marketing consent alone must NOT open this gate (CD-8600): these
 * are analytics events (GA4 is gated on `analytics_storage`), and the once-only
 * signup consent-grant replay keys off this function — if marketing consent
 * opened the gate, the replay would fire under marketing-only consent and,
 * because it fires at most once, GA4 would never receive a properly-consented
 * landing event when Analytics is accepted afterwards.
 */
export const hasAnalyticsConsent = (): boolean =>
  readConsentCookie()?.optionalCookies?.[ANALYTICS_CATEGORY] === 'accepted';
